import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { avatars } from '../../shared/constants';
import { api } from '../api';
import { Brand, Button, Card, GameIcon, Notice, Shell } from '../components';

type Mode = 'join' | 'register' | 'login';

export function JoinPage() {
  const params = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [mode, setMode] = useState<Mode>(params.code ? 'register' : 'join');
  const [code, setCode] = useState((params.code ?? '').toUpperCase());
  const [gameName, setGameName] = useState('');
  const [nickname, setNickname] = useState('');
  const [pin, setPin] = useState('');
  const [avatar, setAvatar] = useState('pumpkin');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function findGame(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const game = await api<{ name: string; registrationOpen: boolean }>(
        '/api/public/games/lookup',
        { method: 'POST', body: JSON.stringify({ code }) },
      );
      setGameName(game.name);
      setMode(game.registrationOpen ? 'register' : 'login');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function authenticate(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const endpoint = mode === 'register' ? '/api/player/register' : '/api/player/login';
      const payload =
        mode === 'register'
          ? { gameCode: code, nickname, pin, avatar }
          : { gameCode: code, nickname, pin };
      await api(endpoint, { method: 'POST', body: JSON.stringify(payload) });
      const next = search.get('next') ?? localStorage.getItem('rave_next') ?? '/play';
      localStorage.removeItem('rave_next');
      navigate(next.startsWith('/') ? next : '/play');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell className="join-shell">
      <header className="hero">
        <Brand />
        <p>SCAN · COMPLETE · EARN · WIN</p>
      </header>
      <Card className="join-card">
        <h1>
          {mode === 'join'
            ? 'Enter the Rave Cave'
            : mode === 'login'
              ? 'Welcome Back'
              : 'Join the Hunt'}
        </h1>
        {gameName && <p className="muted">{gameName}</p>}
        {error && <Notice type="error">{error}</Notice>}
        {mode === 'join' ? (
          <form onSubmit={findGame} className="stack">
            <label>
              Game code
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="RAVE24"
                autoCapitalize="characters"
                required
              />
            </label>
            <Button busy={busy}>Find Game</Button>
          </form>
        ) : (
          <form onSubmit={authenticate} className="stack">
            <label>
              Nickname
              <input
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                maxLength={24}
                autoComplete="username"
                required
              />
            </label>
            {mode === 'register' && (
              <fieldset>
                <legend>
                  Choose an icon
                  {avatar ? (
                    <span className="field-selection">
                      {' '}
                      · {avatar.charAt(0).toUpperCase() + avatar.slice(1)}
                    </span>
                  ) : null}
                </legend>
                <div className="avatar-picker">
                  {avatars.map((item) => (
                    <button
                      type="button"
                      key={item}
                      className={avatar === item ? 'selected' : ''}
                      onClick={() => setAvatar(item)}
                      aria-label={item}
                    >
                      <GameIcon name={item} />
                    </button>
                  ))}
                </div>
              </fieldset>
            )}
            <label>
              4-digit PIN
              <input
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                inputMode="numeric"
                pattern="\d{4}"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                required
              />
            </label>
            <Button busy={busy}>{mode === 'login' ? 'Rejoin Game' : "Let's Go"}</Button>
            <button
              className="text-button"
              type="button"
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
            >
              {mode === 'login' ? 'New player? Register' : 'Already registered? Rejoin'}
            </button>
          </form>
        )}
      </Card>
      <footer>
        Rave Cave · No app required ·{' '}
        <a href="https://pxlkit.xyz" target="_blank" rel="noreferrer">
          Icons by Pxlkit
        </a>
      </footer>
    </Shell>
  );
}
